import React, { useMemo, useState, useCallback, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'

import { rem } from 'polished'
import styled from 'styled-components'
import qs from 'qs'

import { mediaQueryUp } from 'components/utils/breakpoint'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'

import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'

import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import * as Analytics from 'analytics/analytics'
import { trackCruisesClickEvent } from 'lib/cruises/cruiseUtils'

const PortsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${rem(24)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(4, 1fr);
  }
`

function CruisePageBrowseByPorts() {
  const [selectedTab, setSelectedTab] = useState<string>('destinations')

  const [destinationsFacets] = useCruiseSearchFacets({ facetTypes: ['destination_ports'] })
  const [departurePortsFacets] = useCruiseSearchFacets({ facetTypes: ['departure_ports'] })

  const [destinationsRef, destinationsInView] = useInView({
    triggerOnce: true,
    threshold: 1,
  })
  const [departurePortsRef, departurePortsInView] = useInView({
    triggerOnce: true,
    threshold: 1,
  })

  const handleTabClick = useCallback((tabName: string) => {
    trackCruisesClickEvent(`Browse by popular ports - ${tabName}`)
    setSelectedTab(tabName)
  }, [])

  const handlePortClick = useCallback((portName: string, portType: 'destination' | 'departure_port') => {
    trackCruisesClickEvent(`Browse by popular ports - ${portType} - ${portName}`)
  }, [])

  const handleViewMoreClick = useCallback(() => {
    trackCruisesClickEvent('Browse by popular ports - View More')
  }, [])

  useEffect(() => {
    if (destinationsInView && selectedTab === 'destinations') {
      Analytics.trackClientEvent({
        subject: 'Browse by popular ports - destinations',
        action: 'viewed',
        category: 'cruise',
        type: 'interaction',
      })
    }
    if (departurePortsInView && selectedTab === 'departurePorts') {
      Analytics.trackClientEvent({
        subject: 'Browse by popular ports - departurePorts',
        action: 'viewed',
        category: 'cruise',
        type: 'interaction',
      })
    }
  }, [destinationsInView, departurePortsInView, selectedTab])

  const mostPopularDestinations = useMemo(() => {
    return destinationsFacets
      // this filter is used to ignore high level destinations like "Europe", we need to update BE to return destination type
      .filter((facet) => facet.name.includes(','))
      .map((facet) => ({
        ...facet,
        name: facet.name.split(',')[0],
      }))
      .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
      .slice(0, 12)
  }, [destinationsFacets])

  const mostPopularDeparturePorts = useMemo(() => {
    return departurePortsFacets
      // this filter is used to ignore high level destinations like "Europe", we need to update BE to return destination type
      .filter((facet) => facet.name.includes(','))
      .map((facet) => ({
        ...facet,
        name: facet.name.split(',')[0],
      }))
      .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
      .slice(0, 12)
  }, [departurePortsFacets])

  return <Group gap={32} direction="vertical">
    <LayoutContainer>
      <Group direction="vertical" gap={16}>
        <Heading variant="heading3">
          Browse by popular ports
        </Heading>

        <TabButtonsGroup>
          <TabButton
            isActive={selectedTab === 'destinations'}
            onClick={() => handleTabClick('destinations')}
            size="large"
          >
            Destinations
          </TabButton>

          <TabButton
            isActive={selectedTab === 'departurePorts'}
            onClick={() => handleTabClick('departurePorts')}
            size="large"
          >
            Departure ports
          </TabButton>
        </TabButtonsGroup>
      </Group>
    </LayoutContainer>

    <LayoutContainer>
      {selectedTab === 'destinations' && (
        <PortsGrid ref={destinationsRef}>
          {mostPopularDestinations.map((facet) => (
            <Group key={facet.value} direction="vertical" gap={8} horizontalAlign="start">
              <BodyText variant="medium" weight="bold">Cruises to {facet.name}</BodyText>
              <TextLink
                size="caption"
                underline
                colour="neutral-three"
                to={`/search/cruises?${qs.stringify({
                  destinationNames: [facet.name],
                  destinationIds: [facet.value],
                }, { arrayFormat: 'repeat' })}`}
                onClick={() => handlePortClick(facet.name, 'destination')}
              >
                View {facet.count} cruises
              </TextLink>
            </Group>
          ))}
        </PortsGrid>
      )}

      {selectedTab === 'departurePorts' && (
        <PortsGrid ref={departurePortsRef}>
          {mostPopularDeparturePorts.map((facet) => (
            <Group key={facet.value} direction="vertical" gap={8} horizontalAlign="start">
              <BodyText variant="medium" weight="bold">Crusies from {facet.name}</BodyText>
              <TextLink
                size="caption"
                underline
                colour="neutral-three"
                to={`/search/cruises?${qs.stringify({
                  departurePortNames: [facet.name],
                  departurePortIds: [facet.value],
                }, { arrayFormat: 'repeat' })}`}
                onClick={() => handlePortClick(facet.name, 'departure_port')}
              >
                View {facet.count} cruises
              </TextLink>
            </Group>
          ))}
        </PortsGrid>
      )}
    </LayoutContainer>

    <Group direction="horizontal" horizontalAlign="center">
      <TextButton
        to="/search/cruises"
        kind="secondary"
        size="large"
        onClick={handleViewMoreClick}
      >
        View more
      </TextButton>
    </Group>
  </Group>
}

export default CruisePageBrowseByPorts
